import type { CostJob, CostKind } from "@/lib/api/types";
import { consumptionText } from "@/lib/cost-report";
import { formatCount, formatDateTime, formatEuro } from "@/lib/format";

const CARD = "rounded-lg border border-line bg-white";
const HEAD = "flex flex-col gap-0.5 border-b border-[#e6e9e7] px-5 py-4";

/** Verbrauch je Funktion (Art des Jobs): Wo gehen die Abrufe hin? */
export function KindsTable({ kinds }: { kinds: CostKind[] }) {
  return (
    <section aria-labelledby="kinds-title" className={CARD}>
      <div className={HEAD}>
        <h2 id="kinds-title" className="text-base font-semibold">
          Nach Funktion
        </h2>
        <span className="text-xs text-muted">Welche Funktion wie viel verbraucht hat, die teuerste zuerst.</span>
      </div>
      {kinds.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted">In diesem Monat gab es keine Abrufe.</p>
      ) : (
        <div className="overflow-x-auto">
          <table aria-label="Kosten nach Funktion" className="w-full min-w-[720px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="w-full px-5 py-2.5 font-medium">Funktion</th>
                <th className="px-3 py-2.5 text-right font-medium">Jobs</th>
                <th className="px-3 py-2.5 text-right font-medium">Bezahlte Abrufe</th>
                <th className="px-3 py-2.5 text-right font-medium">Euro-Kosten</th>
                <th className="px-5 py-2.5 font-medium">Verbrauch</th>
              </tr>
            </thead>
            <tbody>
              {kinds.map((row) => (
                <tr key={row.kind || "ohne-job"} className="border-t border-neutral-bg align-top">
                  <th scope="row" className="px-5 py-3 text-left font-medium">
                    {row.label}
                  </th>
                  <td className="px-3 py-3 text-right font-mono text-[13px]">{row.kind ? formatCount(row.jobs) : "–"}</td>
                  <td className="px-3 py-3 text-right font-mono text-[13px]">{formatCount(row.paid_requests)}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-[13px]">
                    {row.cost_eur > 0 ? formatEuro(row.cost_eur) : "–"}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-[13px] text-[#3a424a]">{consumptionText(row.consumption)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

const JOB_STATUS: Record<string, string> = {
  queued: "Wartet",
  running: "Läuft",
  awaiting_approval: "Wartet auf Freigabe",
  succeeded: "Fertig",
  failed: "Fehlgeschlagen",
  cancelled: "Abgebrochen",
};

/** Die letzten Jobs des Monats mit ihrem Verbrauch. */
export function JobsTable({ jobs }: { jobs: CostJob[] }) {
  return (
    <section aria-labelledby="jobs-title" className={CARD}>
      <div className={HEAD}>
        <h2 id="jobs-title" className="text-base font-semibold">
          Letzte Jobs
        </h2>
        <span className="text-xs text-muted">Die zehn neuesten Jobs mit Kosten in diesem Monat.</span>
      </div>
      {jobs.length === 0 ? (
        <p className="px-5 py-6 text-sm text-muted">In diesem Monat gab es keine Jobs mit Kosten.</p>
      ) : (
        <div className="overflow-x-auto">
          <table aria-label="Letzte Jobs" className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-muted">
                <th className="whitespace-nowrap px-5 py-2.5 font-medium">Zeitpunkt</th>
                <th className="px-3 py-2.5 font-medium">Funktion</th>
                <th className="w-full px-3 py-2.5 font-medium">Bezug</th>
                <th className="px-3 py-2.5 font-medium">Status</th>
                <th className="px-3 py-2.5 text-right font-medium">Euro-Kosten</th>
                <th className="px-5 py-2.5 font-medium">Verbrauch</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-t border-neutral-bg align-top">
                  <td className="whitespace-nowrap px-5 py-3 text-[#3a424a]">{formatDateTime(job.created_at)}</td>
                  <th scope="row" className="px-3 py-3 text-left font-medium">
                    {job.label}
                  </th>
                  <td className="break-words px-3 py-3 text-[13px] text-[#3a424a]">{job.context ?? "–"}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-[13px]">{JOB_STATUS[job.status] ?? job.status}</td>
                  <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-[13px]">
                    {job.cost_eur > 0 ? formatEuro(job.cost_eur) : "–"}
                  </td>
                  <td className="whitespace-nowrap px-5 py-3 text-[13px] text-[#3a424a]">{consumptionText(job.consumption)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
